import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { fetchLessonsForDomain } from "@/lib/learning";

type Lesson = {
  id: string;
  title: string;
  description?: string;
  summary?: string;
  level: string;
  domainId: string;
  domainName?: string;
  [key: string]: any;
};

type LessonDataContextType = {
  lessonsCache: Map<string, Lesson[]>;
  getLessonsForDomain: (domainId: string, domainName?: string) => Promise<Lesson[]>;
  clearCache: () => void;
  isCached: (domainId: string) => boolean;
};

const LessonDataContext = createContext<LessonDataContextType | undefined>(undefined);

export function LessonDataProvider({ children }: { children: ReactNode }) {
  // Cache lessons by domain ID
  const [lessonsCache, setLessonsCache] = useState<Map<string, Lesson[]>>(new Map());

  const getLessonsForDomain = useCallback(async (domainId: string, domainName?: string): Promise<Lesson[]> => {
    // Return from cache if available
    if (lessonsCache.has(domainId)) {
      return lessonsCache.get(domainId)!;
    }

    // Fetch from Firestore if not cached
    try {
      const lessons = await fetchLessonsForDomain(domainId);
      const normalizedLessons = lessons.map((lesson: any) => ({
        id: lesson.id || '',
        title: lesson.title || '',
        description: lesson.description || lesson.summary || '',
        summary: lesson.summary || lesson.description || '',
        level: lesson.level || 'intro',
        domainId: domainId,
        domainName: domainName,
        ...lesson
      }));

      // Update cache
      setLessonsCache(prev => new Map(prev).set(domainId, normalizedLessons));
      
      return normalizedLessons;
    } catch (err) {
      console.error(`Error fetching lessons for ${domainId}:`, err);
      return [];
    }
  }, [lessonsCache]);

  const clearCache = useCallback(() => {
    setLessonsCache(new Map());
  }, []);

  const isCached = useCallback((domainId: string) => {
    return lessonsCache.has(domainId);
  }, [lessonsCache]);

  const value = {
    lessonsCache,
    getLessonsForDomain,
    clearCache,
    isCached
  };

  return (
    <LessonDataContext.Provider value={value}>
      {children}
    </LessonDataContext.Provider>
  );
}

export function useLessonData() {
  const context = useContext(LessonDataContext);
  if (context === undefined) {
    throw new Error("useLessonData must be used within a LessonDataProvider");
  }
  return context;
}
